import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {NavBar, Skeleton, Image, Divider} from 'antd-mobile';
import { TruckOutline, AppstoreOutline, TransportQRcodeOutline, ExclamationCircleOutline, CalendarOutline } from 'antd-mobile-icons';
import EmptyState from '../../../../components/EmptyState';
import { useTranslation } from 'react-i18next';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import { t } from '../../../../i18n';
import { useTransportStore } from '../../store/transportStore';
import { formatTimestampByLanguage } from '../../../../utils/dateTimeUtils';
import TransportPhotoViewer from '../../components/TransportPhotoViewer';
import WeatherIcon from '../../components/WeatherIcon';

interface TransportExceptionReportedPageProps {}

// 异常原因类型映射
const getExceptionReasonInfo = (type: number, t: (key: string) => string) => {
    const iconStyle = { fontSize: 20, color: '#ef4444' };
    const weatherIconStyle = { fontSize: 24, width: '1em', height: '1em', color: '#ef4444' };
    switch (type) {
        case 1:
            return { 
                icon: <TransportQRcodeOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.vehicleBreakdown') 
            };
        case 2:
            return { 
                icon: <ExclamationCircleOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.trafficAccident') 
            };
        case 3:
            return { 
                icon: <WeatherIcon style={weatherIconStyle} />, 
                label: t('pages.tms.exception.reasons.weather') 
            };
        case 4:
            return { 
                icon: <TruckOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.trafficJam') 
            };
        case 5:
            return { 
                icon: <CalendarOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.warehouseDelay') 
            };
        case 9:
            return { 
                icon: <AppstoreOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.other') 
            };
        default:
            return { 
                icon: <AppstoreOutline style={iconStyle} />, 
                label: t('pages.tms.exception.reasons.other') 
            };
    }
};

const TransportExceptionReportedPage: React.FC<TransportExceptionReportedPageProps> = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);

    const { tmsExceptions, loadingExceptions, fetchTmsExceptionsByCurrentAccount } = useTransportStore(state => ({
        tmsExceptions: state.tmsExceptions,
        loadingExceptions: state.loadingExceptions,
        fetchTmsExceptionsByCurrentAccount: state.fetchTmsExceptionsByCurrentAccount
    }));

    // 初始加载
    useEffect(() => {
        fetchTmsExceptionsByCurrentAccount();
    }, [fetchTmsExceptionsByCurrentAccount]);

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {t('pages.tms.exceptionRecord')}
                </NavBar>
            </div>

            {/* 异常记录列表 */}
            <div className="flex-1 overflow-y-auto px-3 py-3">
                {loadingExceptions ? (
                    <div className="p-4">
                        <Skeleton animated />
                        <Skeleton animated />
                        <Skeleton animated />
                    </div>
                ) : tmsExceptions.length === 0 ? (
                    <EmptyState />
                ) : (
                    <div className="space-y-3">
                        {tmsExceptions.map((exception) => {
                            const reasonInfo = getExceptionReasonInfo(exception.exceptionReasonType, t);
                            // 参照 TransportFinishedDetailPage#transportDetailPhotos，直接使用 exceptionPhotosUrlsJson
                            const photos = (() => {
                                if (exception.exceptionPhotosUrlsJson && exception.exceptionPhotosUrlsJson.length > 0) {
                                    return exception.exceptionPhotosUrlsJson;
                                }
                                return [];
                            })();

                            return (
                                <div
                                    key={exception.id}
                                    className="bg-white rounded-lg shadow-sm p-4"
                                >
                                    {/* 异常原因类型 */}
                                    <div className="flex items-center mb-3">
                                        <div className="mr-2 flex items-center">{reasonInfo.icon}</div>
                                        <span className="text-base font-medium text-red-500">
                                            {reasonInfo.label}
                                        </span>
                                    </div>
                                    <Divider/>
                                    {/* 时间和地点 */}
                                    <div className="mb-2">
                                        <div className="text-sm text-gray-700">
                                            {t('pages.tms.exception.taskNumberLabel')}: {exception.taskNumber || '-'}
                                        </div>
                                    </div>
                                    {/* 时间和地点 */}
                                    <div className="mb-2">
                                        <div className="text-sm text-gray-700 mb-1">
                                            {t('pages.tms.exception.time')}: {exception.createdAt
                                            ? formatTimestampByLanguage(exception.createdAt, i18n.language)
                                            : '-'}
                                        </div>
                                        <div className="text-sm text-gray-700">
                                            {t('pages.tms.exception.locationLabel')}: {exception.location || '-'}
                                        </div>
                                    </div>

                                    {/* 备注 */}
                                    <div className="mb-3">
                                        <div className="text-sm text-gray-700">
                                            {t('pages.tms.exception.remarkLabel')}: {exception.remark || '-'}
                                        </div>
                                    </div>

                                    {/* 照片 */}
                                    {photos.length > 0 && (
                                        <div className="grid grid-cols-3 gap-2 mt-3">
                                            {photos.map((photoUrl, index) => (
                                                <div
                                                    key={index}
                                                    className="aspect-square overflow-hidden bg-white border border-gray-200 rounded-lg"
                                                >
                                                    <Image
                                                        src={photoUrl}
                                                        alt={`${reasonInfo.label} ${index + 1}`}
                                                        fit="cover"
                                                        className="w-full h-full object-cover"
                                                        onClick={() => handleViewPhoto(photoUrl)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 查看大图 */}
            <TransportPhotoViewer
                photoUrl={viewingPhoto}
                onClose={() => setViewingPhoto(null)}
            />
        </div>
    );
};

export default TransportExceptionReportedPage;

